/**
 * RunProgressModal — sequential progress UI for a full analysis run.
 *
 * Shared by the Parameters "Run Analysis" button and agent-triggered scenario
 * re-runs, so both surfaces show the same steps and the same failure state.
 *
 * `steps` MUST be the list the orchestrator actually runs (getCalcSteps from
 * Config/calcParamsConfig) — it used to be hardcoded to the receivables list,
 * so payables runs ticked the wrong steps and a mid-run failure left the modal
 * spinning forever.
 */
const RunProgressModal = ({
  steps,
  currentStep,
  stepIndex,
  isDone,
  error,
  title,
  onClose,
}) => {
  const totalSteps = steps.length;
  const pct = isDone ? 100 : Math.round((stepIndex / totalSteps) * 100);
  const failed = Boolean(error);

  const heading = failed
    ? "Calculation Failed"
    : isDone
    ? "Calculations Complete"
    : title || "Running Calculations";

  return (
    <div className="modal is-active">
      <div className="modal-background" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} />
      <div className="modal-card" style={{ maxWidth: "440px", borderRadius: "var(--ev-radius-lg)", overflow: "hidden" }}>
        <header style={{
          background: failed
            ? "linear-gradient(135deg, #A61B2B 0%, #D64550 100%)"
            : "linear-gradient(135deg, #430098 0%, #79589F 100%)",
          padding: "1.2rem 1.5rem",
        }}>
          <p style={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", margin: 0 }}>
            {heading}
          </p>
        </header>

        <section style={{ padding: "1.5rem", backgroundColor: "var(--ev-white)" }}>
          <div style={{ marginBottom: "1.2rem" }}>
            {steps.map((step, i) => {
              const isActive = !isDone && !failed && step.key === currentStep;
              const isFailed = failed && step.key === currentStep;
              const isComplete = !failed && (isDone || i < stepIndex);
              const isPending = !isDone && i > stepIndex;

              return (
                <div
                  key={step.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.45rem 0.5rem",
                    borderRadius: "6px",
                    marginBottom: "0.2rem",
                    backgroundColor: isActive ? "var(--ev-purple-ghost)" : "transparent",
                    transition: "background-color 0.3s",
                  }}
                >
                  <div style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.65rem",
                    flexShrink: 0,
                    backgroundColor: isFailed
                      ? "#D64550"
                      : isComplete ? "#3DB887" : isActive ? "#79589F" : "var(--ev-border)",
                    color: isFailed || isComplete || isActive ? "#fff" : "var(--ev-text-muted)",
                    transition: "all 0.3s",
                  }}>
                    {isFailed ? (
                      <i className="fa-solid fa-xmark" />
                    ) : isComplete ? (
                      <i className="fa-solid fa-check" />
                    ) : isActive ? (
                      <i className="fa-solid fa-spinner fa-spin" />
                    ) : (
                      <span>{i + 1}</span>
                    )}
                  </div>
                  <i className={step.icon} style={{
                    fontSize: "0.8rem",
                    color: isFailed
                      ? "#D64550"
                      : isComplete ? "#3DB887" : isActive ? "#79589F" : "var(--ev-text-muted)",
                    transition: "color 0.3s",
                  }} />
                  <span style={{
                    fontSize: "0.8rem",
                    fontWeight: isActive || isFailed ? 700 : 500,
                    color: isPending ? "var(--ev-text-muted)" : "var(--ev-text)",
                  }}>
                    {step.label}
                  </span>
                  {isActive && (
                    <span style={{
                      marginLeft: "auto",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#79589F",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}>
                      Running
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div style={{
            height: "6px",
            borderRadius: "3px",
            backgroundColor: "var(--ev-border)",
            overflow: "hidden",
            marginBottom: "1rem",
          }}>
            <div style={{
              height: "100%",
              width: `${failed ? 100 : pct}%`,
              borderRadius: "3px",
              background: failed
                ? "linear-gradient(90deg, #A61B2B, #D64550)"
                : isDone
                ? "linear-gradient(90deg, #3DB887, #2FA870)"
                : "linear-gradient(90deg, #430098, #79589F)",
              transition: "width 0.5s ease, background 0.3s",
            }} />
          </div>

          {failed && (
            <div style={{
              padding: "0.6rem 0.75rem",
              borderRadius: "var(--ev-radius)",
              backgroundColor: "#FBE3E5",
              color: "#A61B2B",
              fontSize: "0.78rem",
              lineHeight: 1.45,
              wordBreak: "break-word",
            }}>
              <strong>{currentStep ? `Failed at "${currentStep}"` : "Failed"}</strong>
              <div style={{ marginTop: "0.25rem", fontWeight: 500 }}>{error}</div>
            </div>
          )}

          {isDone && !failed && (
            <div style={{
              textAlign: "center",
              padding: "0.5rem",
              borderRadius: "var(--ev-radius)",
              backgroundColor: "#D8F5E8",
              color: "#1B7A4A",
              fontWeight: 700,
              fontSize: "0.82rem",
            }}>
              All calculations completed successfully
            </div>
          )}
        </section>

        {(isDone || failed) && (
          <footer style={{
            padding: "0.75rem 1.5rem",
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid var(--ev-border)",
            backgroundColor: "var(--ev-surface)",
          }}>
            <button
              className={`button is-small ${failed ? "is-danger" : "is-success"}`}
              onClick={onClose}
              style={{ borderRadius: "6px" }}
            >
              {failed ? "Close" : "Done"}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default RunProgressModal;
